import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme } from '../../ui/theme';
import { OUTFITS_BY_SLOT } from '../../data/outfits';
import { OutfitRenderer } from '../../systems/OutfitRenderer';
import { OutfitIconRenderer } from '../../systems/OutfitIconRenderer';
import { svgStringToTexture } from '../../utils/svgToTexture';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import type { OutfitSlot, HairStyle } from '../../types';

const SLOTS: OutfitSlot[] = ['top', 'bottom', 'hair', 'shoes'];
const ROUND_MS = 45_000;

export class OutfitPuzzleScene extends Phaser.Scene {
  private score = 0; private timeLeft = ROUND_MS;
  private toast!: ToastManager;
  private gameTimer!: Phaser.Time.TimerEvent;
  private timerText!: Phaser.GameObjects.Text; private scoreText!: Phaser.GameObjects.Text;
  private targetSprite: Phaser.GameObjects.Image | null = null;
  private optionContainer: Phaser.GameObjects.Container | null = null;
  private currentTarget: Partial<Record<OutfitSlot, string>> = {};
  private currentSlot: OutfitSlot = 'top';
  private guess: Partial<Record<OutfitSlot, string>> = {};
  private ended = false;
  private progressBar!: Phaser.GameObjects.Rectangle;
  private slotIndicatorText: Phaser.GameObjects.Text | null = null;

  constructor() { super('OutfitPuzzleScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.cameras.main.setBackgroundColor('#fef7e6');
    this.toast = new ToastManager(this);
    this.score = 0; this.timeLeft = ROUND_MS; this.ended = false;

    this.add.text(GAME_WIDTH/2, 72, '🧩 코디 퍼즐', { fontFamily: Theme.font.family, fontSize: '32px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);
    // Score chip
    const sG = this.add.graphics();
    sG.fillStyle(Theme.colors.pink.bg, 1); sG.fillRoundedRect(16, 10, 160, 44, 12);
    sG.lineStyle(1.5, Theme.colors.pink.light, 1); sG.strokeRoundedRect(16, 10, 160, 44, 12);
    this.scoreText = this.add.text(96, 32, '0', { fontFamily: Theme.font.family, fontSize: '24px', fontStyle: '900', color: Theme.hex.text }).setOrigin(0.5);
    // Timer chip
    const tG = this.add.graphics();
    tG.fillStyle(Theme.colors.pink.bg, 1); tG.fillRoundedRect(GAME_WIDTH-176, 10, 160, 44, 12);
    tG.lineStyle(1.5, Theme.colors.pink.light, 1); tG.strokeRoundedRect(GAME_WIDTH-176, 10, 160, 44, 12);
    this.timerText = this.add.text(GAME_WIDTH-96, 32, '45s', { fontFamily: Theme.font.family, fontSize: '18px', fontStyle: '800', color: Theme.hex.pinkDark }).setOrigin(0.5);

    // Progress bar
    const barX = 200, barY = 18, barW = GAME_WIDTH - 400;
    this.add.rectangle(barX + barW / 2, barY, barW, 10, 0xdddddd).setOrigin(0.5);
    this.progressBar = this.add.rectangle(barX, barY, barW, 10, 0x44ee88).setOrigin(0, 0.5);
    new Button(this, 100, GAME_HEIGHT-50, '← 종료', () => this.end(), { width: 120, height: 48, style: 'secondary' });

    // Slot indicator (persistent, updates each round) — centered on option area (right half ~x=880)
    this.slotIndicatorText = this.add.text(GAME_WIDTH * 0.68, 170, '', { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '800', color: Theme.hex.pink }).setOrigin(0.5);

    this.gameTimer = this.time.addEvent({ delay: 100, loop: true, callback: () => this.tick() });
    await this.startRound();
  }

  private tick(): void {
    this.timeLeft -= 100;
    const secs = Math.ceil(this.timeLeft / 1000);
    this.timerText.setText(`${secs}s`);
    const ratio = Math.max(0, this.timeLeft / ROUND_MS);
    const barW = GAME_WIDTH - 400;
    this.progressBar.setDisplaySize(barW * ratio, 10);
    const r = Math.min(255, Math.floor((1 - ratio) * 510));
    const g = Math.min(255, Math.floor(ratio * 510));
    this.progressBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 50));
    if (this.timeLeft <= 0) this.end();
  }

  private async startRound(): Promise<void> {
    // 랜덤 타겟 코디 (4 슬롯)
    this.currentTarget = {};
    this.guess = {};
    this.currentSlot = SLOTS[0];
    for (const slot of SLOTS) {
      const items = OUTFITS_BY_SLOT[slot] ?? [];
      this.currentTarget[slot] = items[Math.floor(Math.random() * items.length)].id;
    }
    // 타겟 캐릭터 표시 (full character — keep as is)
    if (this.targetSprite) this.targetSprite.destroy();
    const hairId = this.currentTarget.hair;
    const fullKey = hairId ? `char_full_${hairId}` : null;
    const useKey = fullKey && this.textures.exists(fullKey) ? fullKey : 'character_bald';
    if (this.textures.exists(useKey)) {
      this.targetSprite = this.add.image(280, GAME_HEIGHT/2, useKey).setScale(0.7);
    } else {
      const equipped = OutfitRenderer.resolveEquipped(this.currentTarget as Record<OutfitSlot,string>, Object.fromEntries(Object.values(OUTFITS_BY_SLOT).flat().map(o=>[o.id,o])));
      const svg = OutfitRenderer.renderCharacterSVG(equipped);
      const key = 'puzzle_target_main';
      await svgStringToTexture(this, key, svg);
      this.targetSprite = this.add.image(280, GAME_HEIGHT/2, key).setScale(1.5);
    }
    this.add.text(280, GAME_HEIGHT-200, '이 코디를 만드세요!', { fontFamily: Theme.font.family, fontSize: '16px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0.5);

    await this.renderOptions();
  }

  private async renderOptions(): Promise<void> {
    if (this.optionContainer) this.optionContainer.destroy();
    this.optionContainer = this.add.container(0, 0);

    // Update slot indicator
    if (this.slotIndicatorText) {
      const slotNames: Record<OutfitSlot, string> = { top:'상의', bottom:'하의', hair:'헤어', hairpin:'머리삔', glasses:'안경', bag:'가방', shoes:'신발' };
      this.slotIndicatorText.setText(`${slotNames[this.currentSlot]} 고르기`);
    }

    // 4지 선다 (정답 1, 오답 3)
    const items = OUTFITS_BY_SLOT[this.currentSlot] ?? [];
    const correctId = this.currentTarget[this.currentSlot]!;
    const others = items.filter(i => i.id !== correctId).sort(() => Math.random() - 0.5).slice(0, 3);
    const options = [items.find(i => i.id === correctId)!, ...others].sort(() => Math.random() - 0.5);

    // Option grid: 2×2, centered on right 55% of screen
    const optAreaCx = GAME_WIDTH * 0.68;
    await Promise.all(options.map(async (o, i) => {
      const x = optAreaCx - 110 + (i % 2) * 220;
      const y = 300 + Math.floor(i / 2) * 220;

      // Card background
      const card = this.add.graphics();
      card.fillStyle(parseInt(o.color.slice(1), 16), 0.15);
      card.lineStyle(3, 0x3a2540);
      card.fillRoundedRect(x - 90, y - 90, 180, 180, 12);
      card.strokeRoundedRect(x - 90, y - 90, 180, 180, 12);
      this.optionContainer!.add(card);

      // Outfit icon
      const hairStyle = (o.slot === 'hair' && o.hairStyle) ? o.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(o.slot, o.color, hairStyle, o.iconStyle);
      const iconKey = `puzzle_opt_${o.id}`;
      try {
        await svgStringToTexture(this, iconKey, iconSvg);
        if (!this.scene.isActive() || !this.optionContainer) return;
        const icon = this.add.image(x, y - 20, iconKey).setScale(4.0);
        this.optionContainer!.add(icon);
      } catch { /* ignore */ }

      const label = this.add.text(x, y + 70, o.name, { fontFamily: Theme.font.family, fontSize: '13px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0.5);
      this.optionContainer!.add(label);

      const hit = this.add.rectangle(x, y, 180, 180, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => void this.pick(o.id, card, x, y));
      this.optionContainer!.add(hit);
    }));
  }

  private async pick(outfitId: string, card: Phaser.GameObjects.Graphics, x: number, y: number): Promise<void> {
    const correct = this.currentTarget[this.currentSlot] === outfitId;
    if (correct) {
      this.score += 15;
      AudioManager.playSfx('correct');
      this.toast.show('✓ 정답!', 'gold');
      // Green flash on card
      card.clear();
      card.fillStyle(0x44ee88, 0.5);
      card.lineStyle(5, 0x44ee88);
      card.fillRoundedRect(x - 90, y - 90, 180, 180, 12);
      card.strokeRoundedRect(x - 90, y - 90, 180, 180, 12);
    } else {
      this.score = Math.max(0, this.score - 5);
      AudioManager.playSfx('wrong');
      this.toast.show('❌');
      // Red flash
      card.clear();
      card.fillStyle(0xe54e87, 0.4);
      card.lineStyle(5, 0xe54e87);
      card.fillRoundedRect(x - 90, y - 90, 180, 180, 12);
      card.strokeRoundedRect(x - 90, y - 90, 180, 180, 12);
    }
    this.scoreText.setText(`${this.score}`);
    this.guess[this.currentSlot] = outfitId;

    // 다음 슬롯
    const idx = SLOTS.indexOf(this.currentSlot);
    await new Promise<void>(resolve => this.time.delayedCall(500, resolve));
    if (idx === SLOTS.length - 1) {
      // 라운드 종료
      this.toast.show('다음 라운드!', 'gold');
      this.time.delayedCall(800, () => void this.startRound());
    } else {
      this.currentSlot = SLOTS[idx + 1];
      await this.renderOptions();
    }
  }

  private end(): void {
    if (this.ended) return; this.ended = true;
    this.gameTimer.remove();
    const reward = MiniGameRunner.reportResult('outfitPuzzle', this.score);
    this.cameras.main.fadeOut(200);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.toast.show(`🎉 ${this.score}점! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
      this.time.delayedCall(1800, () => this.scene.start('MiniGameHubScene'));
    });
  }
}
